import { memo, useEffect, useState } from "react";
import { Container, Typography, Box, Button, CircularProgress, Alert } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation, useLazyQuery } from "@apollo/client";
import { VERIFY_EMAIL } from "@auth/graphql/mutations/verifyEmail";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import { Mutation, MutationVerifyEmailArgs } from "@/app/graphql/codegen";
import { RouteTo } from "@routing/RouteMap";
import { useDispatch } from "react-redux";
import { showSuccessNotification, handleErrorWithNotification } from "@/utils/errorHandling";
import { gql } from "@apollo/client";
import { UNIFIED_CHECKOUT } from "@shop/graphql/mutations/unifiedCheckout";

const VerifyEmailPage = memo(() => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [verificationState, setVerificationState] = useState<'verifying' | 'success' | 'error' | 'missing-token'>('verifying');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [verifyEmailMutation] = useMutation<Pick<Mutation, "verifyEmail">, MutationVerifyEmailArgs>(VERIFY_EMAIL);

    // Lazy query to load any saved checkout state
    const GET_SAVED_CHECKOUT_STATE = gql`
        query SavedCheckoutState {
            savedCheckoutState
        }
    `;

    const [fetchCheckoutState] = useLazyQuery(GET_SAVED_CHECKOUT_STATE);

    // Mutation to resume unified checkout directly
    const [unifiedCheckout] = useMutation(UNIFIED_CHECKOUT);

    const token = searchParams.get('token');

    useEffect(() => {
        const verifyEmail = async () => {
            if (!token) {
                setVerificationState('missing-token');
                return;
            }

            try {
                const result = await verifyEmailMutation({
                    variables: { token },
                    refetchQueries: [{ query: FETCH_USER_PROFILE }]
                });

                if (result.data?.verifyEmail) {
                    // First set UI state
                    setVerificationState('success');
                    showSuccessNotification(
                        "Ihre E-Mail-Adresse wurde erfolgreich verifiziert!",
                        dispatch,
                        "Verifizierung erfolgreich"
                    );

                    // Try to resume checkout if user has a saved state
                    try {
                        const checkoutRes = await fetchCheckoutState();
                        const saved = checkoutRes.data?.savedCheckoutState as any;

                        if (saved && Array.isArray(saved.selectedProducts) && saved.selectedProducts.length > 0) {
                            // Fire unified checkout mutation directly
                            const ucRes = await unifiedCheckout({
                                variables: {
                                    products: saved.selectedProducts.map((p: any) => ({ priceId: p.priceId, quantity: p.quantity }))
                                }
                            });

                            const resultVal = ucRes.data?.unifiedCheckout;
                            if (typeof resultVal === 'string' && resultVal.startsWith('http')) {
                                // Redirect to Stripe Checkout
                                window.location.href = resultVal;
                                return; // stop further navigation
                            }
                        }
                    } catch (restoreErr) {
                        console.error('Failed to resume checkout automatically', restoreErr);
                    }
                }
            } catch (error) {
                console.error('Email verification error:', error);
                setVerificationState('error');
                
                // Extract error message
                const errorMsg = error instanceof Error ? error.message : 'Unbekannter Fehler';
                setErrorMessage(errorMsg);
                
                handleErrorWithNotification(
                    error as Error,
                    dispatch,
                    "Verifizierung fehlgeschlagen",
                    "Die E-Mail-Verifizierung konnte nicht abgeschlossen werden"
                );
            }
        };

        verifyEmail();
    }, [token, verifyEmailMutation, dispatch, fetchCheckoutState, unifiedCheckout]);

    const handleGoToDashboard = () => {
        navigate(RouteTo.DASHBOARD);
    };

    const handleGoToLogin = () => {
        navigate(RouteTo.LOGIN);
    };

    return (
        <Container
            maxWidth="sm"
            sx={{
                minHeight: 'calc(100vh - 142px)', // 100vh minus navbar (88px) and footer (54px)
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4, // add some vertical padding for smaller viewports
            }}
        >
            <Box sx={{ textAlign: 'center', width: '100%' }}>
                {verificationState === 'verifying' && (
                    <Box>
                        <CircularProgress size={60} sx={{ mb: 3 }} />
                        <Typography variant="h4" sx={{ mb: 2 }}>
                            E-Mail wird verifiziert...
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Bitte warte einen Moment.
                        </Typography>
                    </Box>
                )}

                {verificationState === 'success' && (
                    <Box>
                        <Typography variant="h4" sx={{ mb: 2, color: 'success.main' }}>
                            ✅ Verifizierung erfolgreich!
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Ihre E-Mail-Adresse wurde erfolgreich verifiziert. Sie können jetzt alle Funktionen von ESGbot nutzen.
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleGoToDashboard}
                            size="large"
                        >
                            Zum Dashboard
                        </Button>
                    </Box>
                )}

                {verificationState === 'error' && (
                    <Box>
                        <Typography variant="h4" sx={{ mb: 2, color: 'error.main' }}>
                            ❌ Verifizierung fehlgeschlagen
                        </Typography>
                        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                            <Typography variant="body1">
                                Die E-Mail-Verifizierung konnte nicht abgeschlossen werden.
                            </Typography>
                            {errorMessage && (
                                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                    Fehler: {errorMessage}
                                </Typography>
                            )}
                        </Alert>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Mögliche Gründe:
                        </Typography>
                        <Box component="ul" sx={{ textAlign: 'left', mb: 3, mx: 'auto', maxWidth: 400 }}>
                            <li>Der Verifizierungslink ist abgelaufen (24 Stunden)</li>
                            <li>Der Link wurde bereits verwendet</li>
                            <li>Der Link ist ungültig oder beschädigt</li>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                onClick={handleGoToLogin}
                            >
                                Zur Anmeldung
                            </Button>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={handleGoToDashboard}
                            >
                                Zum Dashboard
                            </Button>
                        </Box>
                    </Box>
                )}

                {verificationState === 'missing-token' && (
                    <Box>
                        <Typography variant="h4" sx={{ mb: 2, color: 'warning.main' }}>
                            ⚠️ Kein Verifizierungstoken
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Dieser Link ist ungültig. Bitte verwenden Sie den vollständigen Verifizierungslink aus Ihrer E-Mail.
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleGoToLogin}
                            size="large"
                        >
                            Zur Anmeldung
                        </Button>
                    </Box>
                )}
            </Box>
        </Container>
    );
});

export default VerifyEmailPage; 